import { Injector } from '@angular/core';
import { Store } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { find, isEmpty } from '@sama/micro-dash';
import { cache, SubscriptionManager } from '@sama/rxjs-core';
import {
  BehaviorSubject,
  combineLatest,
  lastValueFrom,
  Observable,
  Subject,
} from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take,
} from 'rxjs/operators';
import { BrowserControlService } from '../../../../../cross-team-shared/misc-services/browser-control.service';
import { isOutputWithAnswerOptions } from '../../../../../cross-team-shared/misc-utils/output-utils';
import { AnswersByName } from '../../../../../team-apis/otterdam/answers/answer';
import { AbstractOutput } from '../../../../../team-apis/otterdam/outputs/output';
import { FusionState } from '../../../../h3ml/state/fusion-state';
import {
  buildInspectModeKey,
  getPrimaryOutputId,
  initShapeFilterStore,
} from '../../../../h3ml/state/fusion-state.utils';
import { TaskWorkspaceStore } from '../../../../h3ml/state/task-workspace-store';
import { RootUndoService } from '../../../../shared/misc-services/root-undo.service';
import { SmartLoaderService } from '../../../../shared/misc-services/smart-loader.service';
import { UndoService } from '../toolbars/static-toolbar/undo.service';
import { FusionMinion } from './fusion-minion';
import { ShapeFusionManager } from './shape-fusion-manager';

export class FusionManager extends ShapeFusionManager {
  fusionStore: Store<FusionState>;
  undoService!: UndoService | RootUndoService;

  areAllSingleFrame$: Observable<boolean>;
  primaryMinion$ = new BehaviorSubject<FusionMinion | undefined>(undefined);

  private smartLoaderService: SmartLoaderService;
  private taskWorkspaceStore: TaskWorkspaceStore;
  private browserControlService: BrowserControlService;

  private minionRegistered$ = new Subject<FusionMinion>();

  constructor(
    output: AbstractOutput,
    injector: Injector,
    subscriptionManager: SubscriptionManager,
  ) {
    super(output, injector);

    this.smartLoaderService = injector.get(SmartLoaderService);
    this.taskWorkspaceStore = injector.get(TaskWorkspaceStore);
    this.browserControlService = injector.get(BrowserControlService);
    this.fusionStore = this.taskWorkspaceStore('fusions')(this.output.id);

    this.setUpInitialFusionStateSubscription(subscriptionManager);
    this.setUpSmartLoaderSubscriptions(subscriptionManager);
    this.setUpInspectModeSettingSubscription(subscriptionManager);
    this.areAllSingleFrame$ = this.buildAreAllSingleFrame$();

    this.undoService = this.taskWorkspaceStore.isUsingObjects()
      ? injector.get(RootUndoService)
      : new UndoService(this.taskWorkspaceStore, output);
  }

  // Call when you need to wait for the primary minion to be initialized.
  async sleepUntilPrimaryIsAvailable() {
    return this.sleepUntilMinionIsAvailable(getPrimaryOutputId(this.output));
  }

  async sleepUntilMinionIsAvailable(outputId: number) {
    const minionAlreadyRegistered = this.getMinion(outputId);

    return minionAlreadyRegistered
      ? Promise.resolve(minionAlreadyRegistered)
      : lastValueFrom(
          this.minionRegistered$.pipe(
            filter((minion) => minion.output.id === outputId),
            take(1),
          ),
        );
  }

  getContentConnector(outputName: string) {
    const minion = this.getMinionByName(outputName);

    assert(minion, 'minion should be registered when getting contentConnector');

    return minion.contentConnector;
  }

  getPrimaryMinion() {
    const primaryMinion = this.primaryMinion$.value;
    assert(primaryMinion, 'Fetched primaryMinion before it was initialized');
    return primaryMinion;
  }

  getFrameNumberForOutput$(outputName: string) {
    return this.fusionStore('frameNumber').$.pipe(
      map(() => this.getFrameNumberForOutput(outputName)),
      distinctUntilChanged(),
    );
  }

  getFrameNumberForOutput(outputName: string) {
    return Math.min(
      this.fusionStore('frameNumber').state(),
      this.taskWorkspaceStore.getMaxFrameNumber(outputName),
    );
  }

  scrubTo(frameNumber: number) {
    if (Number.isNaN(frameNumber)) {
      return;
    }
    this.fusionStore('frameNumber').set(frameNumber);
  }

  isShapeFused(index: number) {
    return this.minions.filter((minion) => minion.hasShape(index)).length > 1;
  }

  getMinion(outputId: number) {
    return this.minions.find((minion) => minion.getOutputId() === outputId);
  }

  getMinionByName(outputName: string) {
    return this.minions.find((minion) => minion.output.name === outputName);
  }

  override registerMinion(minion: FusionMinion) {
    super.registerMinion(minion);
    if (minion.output.id === getPrimaryOutputId(this.output)) {
      this.primaryMinion$.next(minion);
    }
    this.minionRegistered$.next(minion);
    this.undoService.addChild(minion.undoService);
    this.undoService.addChild(minion.contentConnector);
  }

  override unregisterMinion(minion: FusionMinion) {
    super.unregisterMinion(minion);
    this.undoService.removeChild(minion.undoService);
    this.undoService.removeChild(minion.contentConnector);
  }

  validateAnswer(answers: AnswersByName, cloudOutputName: string) {
    const activeLayerStore =
      this.getPrimaryMinion().store.getActiveShapeLayerStore()!;
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!activeLayerStore) {
      return [];
    }
    const pointCloudAnswer = find(
      answers,
      (_, name) => name === cloudOutputName,
    );

    return this.wasAnswerCleared(pointCloudAnswer);
  }

  private buildAreAllSingleFrame$() {
    return this.minionRegistered$.pipe(
      startWith(undefined),
      switchMap(() =>
        combineLatest(this.minions.map((minion) => minion.hasSingleFrame$())),
      ),
      map((minionsHasSingleFrame) =>
        minionsHasSingleFrame.every((hasSingleFrame) => hasSingleFrame),
      ),
      cache(),
    );
  }

  private setUpInitialFusionStateSubscription(
    subscriptionManager: SubscriptionManager,
  ) {
    subscriptionManager.subscribeTo(
      combineLatest([
        this.taskWorkspaceStore('project').$.pipe(filter(isDefined), take(1)),
        this.taskWorkspaceStore('task')('id').$,
      ]),
      () => {
        this.taskWorkspaceStore('fusions')(this.output.id).set(
          this.buildInitialFusionState(),
        );
        this.setInitialInspectMode();
        if (isOutputWithAnswerOptions(this.output)) {
          initShapeFilterStore(this.taskWorkspaceStore, this.output);
        }
      },
    );
  }

  private buildInitialFusionState() {
    const queryParams = new URLSearchParams(
      this.browserControlService.getQueryParameters(),
    );

    const initialState: Partial<FusionState> = {
      frameNumber: Number(queryParams.get('frameNumber')),
    };

    return { ...new FusionState(), ...initialState };
  }

  private setInitialInspectMode() {
    const projectId = this.taskWorkspaceStore.state().project.id;
    const inspectMode =
      localStorage.getItem(`${buildInspectModeKey(projectId)}`) !== 'false';
    this.taskWorkspaceStore('settings')('inspectMode').set(inspectMode);
  }

  private wasAnswerCleared(pointCloudAnswer: any) {
    const shapeInfo =
      this.taskWorkspaceStore.state().outputAnnotationInfo[
        this.getPrimaryMinion().output.name
      ];
    if (
      // @ts-ignore -- fix me
      (!isEmpty(shapeInfo.shapes) || !isEmpty(shapeInfo.groups)) &&
      (isEmpty(pointCloudAnswer) || isEmpty(pointCloudAnswer.shape_groups))
    ) {
      return [
        'Unexpected error occurred while trying to extract answers. Please report this issue.',
      ];
    }
    return [];
  }

  private setUpSmartLoaderSubscriptions(
    subscriptionManager: SubscriptionManager,
  ) {
    subscriptionManager.subscribeTo(
      this.fusionStore('frameNumber').$,
      (pendingFrameNumber) => {
        this.smartLoaderService.setPendingFrameNumber(
          getPrimaryOutputId(this.output),
          pendingFrameNumber,
        );
      },
    );
  }

  private setUpInspectModeSettingSubscription(
    subscriptionManager: SubscriptionManager,
  ) {
    subscriptionManager.subscribeTo(
      combineLatest([
        this.taskWorkspaceStore('settings')('inspectMode').$.pipe(
          filter(isDefined),
        ),
        this.taskWorkspaceStore('project').$.pipe(filter(isDefined), take(1)),
      ]),
      ([inspectMode, project]) => {
        localStorage.setItem(
          `${buildInspectModeKey(project.id)}`,
          inspectMode.toString(),
        );
      },
    );
  }
}
